import * as React from "react";
import {useEffect, useMemo, useState} from "react";
import {Device, DeviceType} from "@variocube/app-api";
import {classNames, useAsync, useAsyncEffect, useCountdown} from "@variocube/cube-ui";
import {stylesheet} from "typestyle";
import {Controller} from "./controller/Controller";
import {unit, UnitStatus} from "./controller/unit";
import * as QRCode from "qrcode";
import {useLocalization} from "../i18n";

interface StatusOverlayProps {
    controller: Controller;
}

export function StatusOverlay({controller}: StatusOverlayProps) {
    const {t} = useLocalization();

    const [controllerConnected, setControllerConnected] = useState(false);
    useEffect(() => {
        controller.addConnectionListener(setControllerConnected);
        return () => controller.removeConnectionListener(setControllerConnected);
    }, [controller, setControllerConnected]);

    const [controllerWasConnected, setControllerWasConnected] = useState(false);
    useEffect(() => {
        if (controllerConnected) {
            setControllerWasConnected(true);
        }
    }, [controllerConnected]);

    const [cubeSetupUrl, setCubeSetupUrl] = useState<string|null>(null);
    useEffect(() => {
        controller.addSetupUrlListener(setCubeSetupUrl);
        return () => controller.removeSetupUrlListener(setCubeSetupUrl);
    }, [controller])

    const [unitConnected, setUnitConnected] = useState(false);
    useEffect(() => {
        unit.connected.add(setUnitConnected);
        return () => unit.connected.remove(setUnitConnected);
    }, []);

    const [unitStatus, setUnitStatus] = useState<UnitStatus>();
    useEffect(() => {
        unit.status.add(setUnitStatus);
        return () => unit.status.remove(setUnitStatus);
    }, []);

    const [devices, setDevices] = useState<Device[]>([]);
    useAsyncEffect(async () => {
        if (controllerConnected) {
            setDevices(await controller.getDevices());
        }
    }, [controller, controllerConnected]);
    useEffect(() => {
        controller.addDeviceListener(setDevices);
        return () => controller.removeDeviceListener(setDevices);
    }, [controller]);

    const unitSetupQr = useAsync(async () => {
        if (unitStatus?.setup) {
            return QRCode.toDataURL(unitStatus?.setup, {margin: 1, width: 360});
        }
    }, [unitStatus?.setup]);

    const lockingGracePeriod = useCountdown(20, controllerConnected);

    const hasLocking = useMemo(() => Boolean(devices.find(device => device.types.indexOf(DeviceType.Locking) >= 0)), [devices]);

    const waitForLocking = useMemo(() => !hasLocking && lockingGracePeriod > 0, [hasLocking, lockingGracePeriod]);

    const cubeSetupUrlQr = useAsync(async () => {
        if (!!cubeSetupUrl) return QRCode.toDataURL(cubeSetupUrl, { margin: 1, width: 360 })
    }, [cubeSetupUrl])

    if (!!cubeSetupUrl) {
        return (
            <div className={classNames(styles.overlay)}>
                <div className={styles.message}>
                    <p className={styles.statusTitle}>Start cube setup</p>
                    <p className={styles.statusText}>Scan this QR code to start cube setup:</p>
                    <img src={cubeSetupUrlQr?.result} alt="Setup QR-Code"/>
                </div>
            </div>
        )
    }

    if (!controllerConnected && unitConnected && unitStatus?.connected && unitStatus.setup) {
        return (
            <div className={classNames(styles.overlay)}>
                <div className={styles.message}>
                    <p className={styles.statusTitle}>Start unit setup</p>
                    <p className={styles.statusText}>Scan this QR code to start unit setup:</p>
                    <img src={unitSetupQr.result} alt="Setup QR-Code"/>
                </div>
            </div>
        )
    }

    if (!controllerWasConnected || waitForLocking) {
        return (
            <div className={classNames(styles.overlay, styles.startupOverlay)}>
                <div className={styles.message}>
                    Starting up...
                </div>
            </div>
        )
    }
    if (!controllerConnected || !hasLocking) {
        return (
            <div className={styles.overlay}>
                <div className={styles.message}>
                    <p className={styles.statusTitle}>{t("OutOfOrderTitle")}</p>
                    <p className={styles.statusText}>{t("OutOfOrderText")}</p>
                </div>
                <div className={styles.details}>
                    {!controllerConnected && "Controller"}
                    {controllerConnected && "Locking hardware"}
                </div>
            </div>
        )
    }
    return null;
}

const styles = stylesheet({
    overlay: {
        position: "fixed",
        top: 0,
        left: 0,
        width: "100%",
        height: "100%",
        background: "#333",
        zIndex: 10000,
    },
    startupOverlay: {
        background: "rgba(0, 0, 0, 0.25)",
    },
    outOfOrderContainer: {
        background: "#333"
    },
    statusTitle: {
        fontSize: "2.5rem",
        margin: "0.75rem",
    },
    statusText: {
        fontSize: "1.25rem"
    },
    message: {
        position: "absolute",
        top: "50%",
        left: "50%",
        transform: "translateX(-50%) translateY(-50%)",
        color: "#fff",
        textAlign: "center"
    },
    details: {
        position: "absolute",
        bottom: "1rem",
        left: 0,
        width: "100%",
        textAlign: "center",
        color: "#fff",
        opacity: 0.5,
    }
});